import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { hotelAPI, paymentsAPI } from "@/lib/api";
import { useTranslation, EUROPEAN_COUNTRIES, POSITIONS_LIST, COUNTRY_FLAGS } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Logo } from "@/components/shared/Logo";
import { ArrowRight, ArrowLeft, CheckCircle2, Phone, Mail, Globe, User } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

export default function HotelOnboarding() {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { t, tp, tc } = useTranslation();
  const [form, setForm] = useState({
    name: "", location: "", country: "", description: "", logo_url: "",
    positions_needed: [], subscription_plan: null,
    contact_person: "", contact_title: "", phone: "",
    corporate_email: "", website: ""
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const togglePosition = (pos) => {
    const current = form.positions_needed;
    update("positions_needed", current.includes(pos) ? current.filter(p => p !== pos) : [...current, pos]);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.location || !form.country || !form.contact_person || !form.phone || !form.corporate_email) {
      toast.error("Error");
      return;
    }
    setSaving(true);
    try {
      await hotelAPI.completeOnboarding(form);
      trackEvent("hotel_onboarding_completed");
      toast.success("OK");
      if (form.subscription_plan) {
        try {
          const res = await paymentsAPI.createCheckout({ plan: form.subscription_plan, payment_type: "subscription" });
          window.location.href = res.data.url;
          return;
        } catch { toast.error("Error"); }
      }
      navigate("/hotel", { replace: true });
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-4" data-testid="hotel-onboarding">
      <div className="w-full max-w-xl">
          <Logo size="lg" className="text-white" />

        <div className="flex items-center gap-2 mb-8 justify-center">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-sans font-bold ${
                step >= s ? "bg-gold-500 text-white" : "bg-navy-800 text-slate-500"
              }`}>{step > s ? <CheckCircle2 className="w-4 h-4" /> : s}</div>
              {s < 4 && <div className={`w-12 h-0.5 ${step > s ? "bg-gold-500" : "bg-navy-800"}`} />}
            </div>
          ))}
        </div>

        <Card className="bg-white shadow-xl">
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="font-sans text-xl">{t("onb.step1_title")}</CardTitle>
                <CardDescription className="font-body">{t("onb.step1_desc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.hotel_name")}</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1 h-11 font-body" data-testid="onb-hotel-name" />
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.city")}</Label>
                  <Input value={form.location} onChange={(e) => update("location", e.target.value)} className="mt-1 h-11 font-body" data-testid="onb-hotel-location" />
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.country")}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {EUROPEAN_COUNTRIES.map(c => (
                      <Badge key={c} variant={form.country === c ? "default" : "outline"}
                        className={`cursor-pointer text-center justify-center py-2 font-body ${form.country === c ? "bg-gold-500 text-white border-gold-500" : "border-slate-300"}`}
                        onClick={() => update("country", c)} data-testid={`country-${c}`}>{COUNTRY_FLAGS[c]} {tc(c)}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.description")}</Label>
                  <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="mt-1 font-body" data-testid="onb-hotel-desc" />
                </div>
                <Button className="w-full bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm h-11" onClick={() => setStep(2)} data-testid="onb-next-1">
                  {t("onb.next")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-sans text-xl">{t("admin.contact_info")}</CardTitle>
                <CardDescription className="font-body">{t("onb.step1_desc_extended")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.contact_person")} *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input value={form.contact_person} onChange={(e) => update("contact_person", e.target.value)} className="h-11 font-body pl-10" data-testid="onb-contact-person" />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.contact_title")} *</Label>
                  <Input value={form.contact_title} onChange={(e) => update("contact_title", e.target.value)} className="mt-1 h-11 font-body" data-testid="onb-contact-title" />
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.phone")} *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-11 font-body pl-10" data-testid="onb-phone" />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.corporate_email")} *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input type="email" value={form.corporate_email} onChange={(e) => update("corporate_email", e.target.value)} className="h-11 font-body pl-10" data-testid="onb-corporate-email" />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-slate-600">{t("onb.website")}</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input value={form.website} onChange={(e) => update("website", e.target.value)} className="h-11 font-body pl-10" placeholder="https://..." data-testid="onb-website" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 h-11 rounded-sm font-body" onClick={() => setStep(1)} data-testid="onb-back-2">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t("onb.back")}
                  </Button>
                  <Button className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm h-11"
                    onClick={() => { if (!form.contact_person || !form.phone || !form.corporate_email) { toast.error("Error"); return; } setStep(3); }}
                    data-testid="onb-next-2">
                    {t("onb.next")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-sans text-xl">{t("onb.step2_title")}</CardTitle>
                <CardDescription className="font-body">{t("onb.step2_desc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {POSITIONS_LIST.map(pos => (
                    <Badge key={pos} variant={form.positions_needed.includes(pos) ? "default" : "outline"}
                      className={`cursor-pointer text-center justify-center py-3 text-sm font-body ${
                        form.positions_needed.includes(pos) ? "bg-gold-500 text-white border-gold-500" : "border-slate-300"
                      }`} onClick={() => togglePosition(pos)} data-testid={`onb-pos-${pos}`}>
                      {tp(pos)}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 h-11 rounded-sm font-body" onClick={() => setStep(2)} data-testid="onb-back-3">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t("onb.back")}
                  </Button>
                  <Button className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm h-11" onClick={() => setStep(4)} data-testid="onb-next-3">
                    {t("onb.next")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="font-sans text-xl">{t("onb.step3_title")}</CardTitle>
                <CardDescription className="font-body">{t("onb.step3_desc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { id: "starter", name: "Starter", price: "49", desc: t("plan.starter.f1"), highlight: false },
                  { id: "professional", name: "Professional", price: "99", desc: t("plan.pro.f1"), highlight: true },
                  { id: "premium", name: "Premium", price: "199", desc: t("plan.premium.f1"), highlight: false },
                ].map(p => (
                  <div key={p.id} className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    form.subscription_plan === p.id ? "border-gold-500 bg-gold-50" : p.highlight ? "border-gold-200" : "border-slate-200 hover:border-slate-300"
                  }`} onClick={() => update("subscription_plan", p.id)} data-testid={`onb-plan-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-sans font-bold text-slate-900">{p.name}</span>
                          {p.highlight && <Badge className="bg-gold-500 text-white text-xs font-body">{t("price.popular")}</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 font-body">{p.desc}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-sans font-bold text-xl text-slate-900">{p.price}</span>
                        <span className="text-xs text-slate-500 font-body">{t("price.month")}</span>
                      </div>
                    </div>
                  </div>
                ))}
                <button className="text-sm text-slate-400 font-body hover:text-slate-600 w-full text-center py-2" onClick={() => update("subscription_plan", null)} data-testid="onb-skip-plan">
                  {t("onb.skip_plan")}
                </button>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1 h-11 rounded-sm font-body" onClick={() => setStep(3)} data-testid="onb-back-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t("onb.back")}
                  </Button>
                  <Button className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm h-11" onClick={handleSubmit} disabled={saving} data-testid="onb-complete">
                    {saving ? t("auth.processing") : t("onb.complete")} <CheckCircle2 className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
